import { memo, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { format } from 'date-fns';
import cx from 'classnames';

import Icon from 'components/Icon';
import { parseResponse } from 'lib/utils/parser';
import { EventTouchpointable } from 'lib/models/event';
import { useOpportunity } from 'lib/providers/OpportunityProvider';

import styles from './EventVenue.module.scss';

type EventVenueProps = {
  className?: string;
};

export const EventVenue: React.FC<EventVenueProps> = memo(({ className }) => {
  const { opportunity } = useOpportunity();
  const [t] = useTranslation('opportunity');
  const { touchpointable: touchpointableResponse, is_online, online_link, place: placeResponse } = opportunity;
  const { start_date } = parseResponse(touchpointableResponse as EventTouchpointable);
  const { location, googleMapsLink } = useMemo(() => {
    const place = !is_online ? parseResponse(placeResponse) : null;
    const location = place?.address;
    const googleMapsLink = place ? `https://www.google.com/maps/place/?q=place_id:${place.google_identifier}` : '';
    return { location, googleMapsLink };
  }, [is_online, placeResponse]);

  const { date, month, dateTime } = useMemo(() => {
    const startTime = new Date(start_date);
    const date = format(startTime, 'dd');
    const month = format(startTime, 'MMM').toUpperCase();
    const dateTime = format(startTime, 'MMM dd, hh:mma');
    return { date, month, dateTime };
  }, [start_date]);

  if (!start_date && !is_online && !location) return <></>;

  return (
    <div className={cx(styles.container, className)}>
      <h2 className={styles.title}>{t('event-venue.title')}</h2>
      <div className={styles['venue-and-address']}>
        {start_date && (
          <div className={cx(styles['info'], styles.card)}>
            <div className={styles.calendar}>
              <p className={styles.month}>{month}</p>
              <p className={styles.date}>{date}</p>
            </div>
            <div className={styles.right}>
              <p className={styles['sub-title']}>{t('event-venue.date-and-time')}</p>
              <div className={styles.data}>{dateTime}</div>
            </div>
          </div>
        )}
        {is_online ? (
          <div className={cx(styles['info'], styles.card)}>
            <div className={styles.pin}>
              <Icon iconName="icon_link" size="auto" />
            </div>
            <div className={styles.right}>
              <p className={styles['sub-title']}>{t('event-venue.meeting-link')}</p>
              {online_link ? (
                <a href={online_link} target="_blank" rel="noreferrer" className={styles['online-link']}>
                  {online_link}
                </a>
              ) : (
                <p className={styles.data}>{t('event-venue.no-meeting-link')}</p>
              )}
            </div>
          </div>
        ) : location ? (
          <div className={cx(styles['info'], styles.card)}>
            <div className={styles.pin}>
              <Icon iconName="icon_pin" size="auto" />
            </div>
            <div className={styles.right}>
              <p className={styles['sub-title']}>{t('event-venue.location')}</p>
              <p className={styles.data}>{location}</p>
              <a href={googleMapsLink} target="_blank" rel="noreferrer" className={styles['get-directions']}>
                <span>{t('event-venue.get-directions')}</span>
                <Icon iconName="icon_arrow-right-2" size="small" />
              </a>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});

EventVenue.displayName = 'EventVenue';
